import { useMutation, useQuery } from "@tanstack/react-query";
import { useEffect, useState } from "react";
import { useTranslations } from "use-intl";

import { FileBrowser } from "@platform/components/ui/file-browser/file-browser";
import type { FileBrowserItem } from "@platform/components/ui/file-browser/file-browser.types";
import { useAuth } from "@platform/auth/context";
import {
	createFolderMutation,
	deleteNodeMutation,
	reorderNodeMutation,
	updateNodeMutation,
	uploadFilesMutation,
} from "../memory.mutations";
import { memoryFilesQueryOptions } from "../memory.queries";

type Crumb = { id: string; name: string };

export function MemoryFiles() {
	const t = useTranslations("memory.files");
	const { can } = useAuth();

	// History of visited directories powers breadcrumbs and back/forward.
	const [history, setHistory] = useState<Crumb[][]>([[]]);
	const [index, setIndex] = useState(0);
	const path = history[index] ?? [];
	const parentId = path.at(-1)?.id ?? null;

	const canWrite = can({ memory: ["write"] });
	const { data: nodes } = useQuery(memoryFilesQueryOptions(parentId));

	const upload = useMutation(uploadFilesMutation);
	const createFolderM = useMutation(createFolderMutation);
	const updateNode = useMutation(updateNodeMutation);
	const reorderNode = useMutation(reorderNodeMutation);
	const deleteNode = useMutation(deleteNodeMutation);

	// Surface whichever write last failed, then auto-dismiss after a few seconds.
	const failure = [upload, createFolderM, updateNode, reorderNode, deleteNode].find(
		(m) => m.error,
	)?.error;
	const [error, setError] = useState<Error | null>(null);

	useEffect(() => {
		if (!failure) return;
		setError(failure);
		const timer = setTimeout(() => setError(null), 5000);
		return () => clearTimeout(timer);
	}, [failure]);

	function navigate(to: Crumb[]) {
		setHistory((h) => [...h.slice(0, index + 1), to]);
		setIndex(index + 1);
	}

	// Only a file the agent does not read carries a note.
	function noteFor(node: NonNullable<typeof nodes>[number]) {
		if (node.kind !== "file") return undefined;
		switch (node.indexStatus) {
			case "indexed":
				return undefined;
			case "unreadable":
				return t("notes.unreadable");
			case "unsupported":
				return t("notes.unsupported");
			case "too_long":
				return t("notes.tooLong");
			case null:
				return t("notes.unread");
		}
	}

	const items: FileBrowserItem[] = (nodes ?? []).map((n) => ({
		id: n.id,
		name: n.name,
		type: n.kind,
		note: noteFor(n),
	}));

	function uploadFiles(list: FileList) {
		const form = new FormData();
		if (parentId) form.append("parentId", parentId);
		for (const file of list) form.append("files", file);
		upload.mutate(form);
	}

	const createFolder = (name: string) => createFolderM.mutate({ name, parentId });
	const rename = (item: FileBrowserItem, name: string) =>
		updateNode.mutate({ id: item.id, patch: { name } });
	const remove = (item: FileBrowserItem) =>
		deleteNode.mutate(item.id, {
			onSuccess: () => {
				if (item.type !== "folder") return;
				// Forward-nav must not re-enter a deleted folder: drop history paths
				// that walked through it, then clamp the cursor.
				const valid = history.filter((p) => !p.some((c) => c.id === item.id));
				setHistory(valid);
				setIndex((i) => Math.min(i, valid.length - 1));
			},
		});
	const move = (item: FileBrowserItem, target: FileBrowserItem) =>
		updateNode.mutate({ id: item.id, patch: { parentId: target.id } });
	const paste = (item: FileBrowserItem) => updateNode.mutate({ id: item.id, patch: { parentId } });
	const reorder = (item: FileBrowserItem, beforeId: string | null) =>
		reorderNode.mutate({ id: item.id, beforeId });

	return (
		<section className="flex flex-col gap-3">
			<div className="flex flex-col gap-1">
				<h2 className="text-lg font-medium">{t("title")}</h2>
				<div className="flex justify-between">
					<p className="text-muted-foreground text-sm">{t("description")}</p>
					{error && <p className="text-destructive text-sm">{error.message || t("error")}</p>}
				</div>
			</div>
			<FileBrowser
				items={items}
				path={path.map((c) => c.name)}
				onOpenFolder={(item) => navigate([...path, { id: item.id, name: item.name }])}
				onNavigate={(i) => navigate(path.slice(0, i + 1))}
				onBack={() => setIndex((i) => Math.max(0, i - 1))}
				onForward={() => setIndex((i) => Math.min(history.length - 1, i + 1))}
				loading={upload.isPending}
				canBack={index > 0}
				canForward={index < history.length - 1}
				onCreateFolder={canWrite ? createFolder : undefined}
				onRename={canWrite ? rename : undefined}
				onDelete={canWrite ? remove : undefined}
				onUpload={canWrite ? uploadFiles : undefined}
				onMove={canWrite ? move : undefined}
				onReorder={canWrite ? reorder : undefined}
				onPaste={canWrite ? paste : undefined}
			/>
		</section>
	);
}
